import { BREAKPOINTS } from '@jouzen/ecom-components/v3/consts/breakpoints';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import type { MessageKey } from 'types/LocaleMessageKey';

import Image from '@/app/components/Image';
import Video from '@/app/components/Video/Video';

import SlideshowItemContent from './SlideshowItemContent';
import type { SlideshowItemProps } from './types';

const AWS_BASE_URL = 'https://s3.amazonaws.com/ouraring.com/images/';

// helper fn to consistently determine whether a file is a video
const VIDEO_EXTENSIONS = ['.mp4'];
const isVideo = (src: string | undefined) =>
  VIDEO_EXTENSIONS.some((ext) => src?.includes(ext));

const videoProps = {
  autoPlay: true,
  contentTitle: 'pdp_video',
  controls: false,
  location: 'styleStep',
  loop: true,
  muted: true,
  playsInline: true,
  type: 'video/mp4',
};

const SlideshowItem = ({
  activeSlide,
  altMsgKey,
  contentTitle,
  contentFormat,
  content,
  showDiscount,
  idx,
  mobileSrc,
  originalSrc,
  originalSrcKey,
  thumbnailSrc,
}: SlideshowItemProps) => {
  const t = useTranslations();

  const isLargeScreen = `(min-width: ${BREAKPOINTS.large}px)`;

  const src = originalSrcKey ? t(originalSrcKey) : originalSrc;
  const imageWidth = !isLargeScreen ? { width: 1024 } : {};
  const imageHeight = !isLargeScreen ? { height: 868 } : {};
  const _mobileVideoSrc = mobileSrc?.includes(AWS_BASE_URL)
    ? mobileSrc.split(AWS_BASE_URL)[1]
    : mobileSrc;
  const _desktopVideoSrc = src.includes(AWS_BASE_URL)
    ? src.split(AWS_BASE_URL)[1]
    : src;

  return (
    <div
      className={cx(
        'pointer-events-none h-full max-h-[60vh] overflow-hidden',
        'lg:flex lg:h-screen lg:max-h-full lg:justify-center',
        {
          'visibility-hidden opacity-0 transition-opacity duration-100':
            activeSlide !== idx,
          'visibility-visible opacity-100 transition-opacity duration-750':
            activeSlide === idx,
        },
      )}
      data-active={activeSlide === idx}
      data-cy={`pdp-slideshow-img-${idx}`}
    >
      {isVideo(src) ? (
        <Video
          {...videoProps}
          altImage={{
            priority: idx === 0,
            src: thumbnailSrc!,
            width: 609,
            height: 457,
          }}
          className="size-full max-h-[60vh] object-cover lg:h-auto lg:max-h-full"
          playVideo={activeSlide === idx}
          srcSet={[
            {
              src: _desktopVideoSrc,
              media: isLargeScreen,
            },
            { src: _mobileVideoSrc as string | undefined },
          ]}
        />
      ) : (
        <Image
          className="object-cover"
          draggable="false"
          // PDP wizard uses a different Layout for large screens, so it needs a different Image setup
          fill={Boolean(isLargeScreen)}
          localizedAlt={altMsgKey as MessageKey}
          priority={idx === 0}
          quality={65}
          sizes="(min-width: 1440px) 100vw, (min-width: 600px) 1024px, (min-width: 400px) 600px, 400px"
          src={src}
          {...imageWidth}
          {...imageHeight}
        />
      )}
      <SlideshowItemContent
        content={content}
        showDiscount={showDiscount}
        contentTitle={contentTitle}
        contentFormat={contentFormat}
      />
    </div>
  );
};

export default SlideshowItem;
